import React from 'react';
import { css } from '@emotion/react';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';

import { scrollTop } from '~/src/common/utilities';
import { LocalizedLink } from '~/src/components/LocalizedLink';
import { typography } from '~/src/styles/design-system';
import { resetStyles } from '~/src/styles/reset-styles-mixins';
import { breakpoints } from '~/src/styles/styled-mixins/utility';
import { fontWeight } from '~/src/styles/styled-variables';

import { ReactComponent as CollapsedIcon } from './collapsed-icon.svg';
import { ReactComponent as ExpandedIcon } from './expanded-icon.svg';
import { useLinkGroups } from './useLinkGroups';

const styles = {
  nav: css({
    [breakpoints.minDesktop]: {
      display: 'flex',
      gap: '40px',
      alignItems: 'baseline',
    },
    [breakpoints.minDesktopLg]: {
      gap: '60px',
    },
  }),
  list: css(resetStyles.ul, typography.mobileBodyTextBold, {
    display: 'flex',
    gap: '16px',
    flexDirection: 'column',
    '--Hover-Font-Weight': fontWeight.bold,
    li: [
      typography.mobileBodyText,
      {
        color: 'var(--Pure-White, #FFF)',
        '&:hover': {
          color: 'var(--Secondary, #2CB3C6)',
          fontFamily: 'Montserrat',
          fontWeight: 'var(--Hover-Font-Weight)',
          fontStyle: 'normal',
          a: {
            textDecoration: 'none',
          },
        },
        '&::after': {
          content: 'attr(data-text) / ""',
          height: 0,
          visibility: 'hidden',
          overflow: 'hidden',
          userSelect: 'none',
          pointerEvents: 'none',
          fontWeight: 'var(--Hover-Font-Weight)',
          display: 'block',
        },
      },
    ],
  }),
  accordion: css`
    box-shadow: none;
    background-color: transparent;
    &:before {
      background-color: transparent;
    }
    &.Mui-expanded {
      margin: 0;
    }

    .MuiCollapse-hidden {
      visibility: hidden;
    }
    .MuiCollapse-root {
      height: 0;
    }
  `,
  accordionDetails: css({
    padding: '16px 0',
    borderBottom: '1px solid var(--Pure-White, #FFF)',
    [breakpoints.minDesktop]: {
      borderBottom: 'none',
    },
  }),

  accordionSummary: css(typography.mobileBodyTextBold, {
    color: 'var(--Pure-White, #FFF)',
    borderBottom: '1px solid var(--Pure-White, #FFF)',
    padding: '16px 0',
    minHeight: 'unset',
    [breakpoints.minDesktop]: {
      gap: '40px',
    },
    '.MuiAccordionSummary-content': {
      whiteSpace: 'nowrap',
      margin: '0',
    },
    '&.Mui-focusVisible': {
      background: 'none',
    },
    '&.Mui-expanded': {
      minHeight: 'unset',
    },
  }),
  icon: css({
    display: 'flex',
    alignItems: 'center',
    '.Mui-expanded & > .collapsedIcon': {
      display: 'none',
    },
    '.expandedIcon': {
      display: 'none',
    },
    '.Mui-expanded & > .expandedIcon': {
      display: 'block',
    },
  }),
};

const Icon: React.FC = () => (
  <div css={styles.icon}>
    <ExpandedIcon className="expandedIcon" />
    <CollapsedIcon className="collapsedIcon" />
  </div>
);

export const Links: React.FC<{ className?: string }> = props => {
  const linkGroups = useLinkGroups();

  return (
    <nav css={styles.nav} {...props}>
      {linkGroups.map(({ title, items }, idx) => (
        <MuiAccordion key={title} css={styles.accordion}>
          <MuiAccordionSummary
            css={styles.accordionSummary}
            style={
              idx === 0
                ? {
                    paddingTop: 0,
                  }
                : {}
            }
            expandIcon={<Icon />}
            aria-label={title}
          >
            {title}
          </MuiAccordionSummary>
          <MuiAccordionDetails css={styles.accordionDetails}>
            <ul css={styles.list}>
              {items.map(item => (
                <li key={item.url} data-text={item.name}>
                  {item.external ? (
                    <a
                      onClick={item.onClick}
                      href={item.url}
                      target={item.onClick ? undefined : '_blank'}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <LocalizedLink to={item.url} onClick={() => scrollTop()}>
                      {item.name}
                    </LocalizedLink>
                  )}
                </li>
              ))}
            </ul>
          </MuiAccordionDetails>
        </MuiAccordion>
      ))}
    </nav>
  );
};
